/**
 * Module: `export/build-response-export-document.ts` — builds export model from chat data.
 */

import { parseCitations } from "@/lib/markdown/citations";
import { parseStructuredResponse } from "@/lib/parsing";
import { splitGatekeeperAnnotations } from "@/lib/parsing/gatekeeper-annotations";
import {
  buildSidoniDocumentUrl,
  resolveSidoniDocResourceUrl,
} from "@/lib/formatting/platform-urls";
import {
  SOURCE_CATEGORY_LABELS,
  SOURCE_TYPE_ORDER,
} from "@/lib/sources/source-categories";
import { capitalizeFirstLetter, stripDustMentionPrefix } from "@/lib/ui-helper";
import type {
  ApiResponse,
  ArticleRef,
  Message,
  SidoniSource,
  Source,
  SourceType,
} from "@/types";

import type {
  ResponseExportDocument,
  ResponseExportSource,
  ResponseExportSourceCategory,
  ResponseExportSourceType,
} from "@/lib/export/response-export-document";
import {
  RESPONSE_EXPORT_DISCLAIMER,
  RESPONSE_EXPORT_TITLE,
} from "@/lib/export/response-export-document";

// Matches canonical `:cite[ids]` and degraded LLM forms preceded by punctuation:
// `; cite[ids]`, `, cite[ids]`. Bare `cite[...]` without prefix is intentionally excluded.
const CITE_MARKER_REGEX = /(?:[;,]\s*cite|:cite)\[[^\]]*\]/g;

const SOURCE_SECTION_REGEX =
  /(?:^|\n)###\s+Source\s+[^:\n]+\s*:[\s\S]*?(?=(?:\n###\s+Source\s+[^:\n]+\s*:)|$)/g;

const SOURCE_TYPE_LABEL: Record<SourceType, ResponseExportSourceType> = {
  sidoni: "SIDONI",
  legifrance: "Légifrance",
  anc: "ANC",
  h2a: "H2A",
  macncc: "MaCNCC",
  cncc: "CNCC",
  formation: "Formation",
};

const EXPORT_TYPE_TO_SOURCE_TYPE: Partial<
  Record<ResponseExportSourceType, SourceType>
> = {
  SIDONI: "sidoni",
  Légifrance: "legifrance",
  ANC: "anc",
  H2A: "h2a",
  MaCNCC: "macncc",
  CNCC: "cncc",
  Formation: "formation",
};

export function stripCitationMarkers(markdown: string): string {
  return markdown
    .replace(CITE_MARKER_REGEX, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function stripMarkdownSourceSections(markdown: string): string {
  if (!markdown.includes("### Source")) return markdown.trim();
  return markdown.replace(SOURCE_SECTION_REGEX, "").trim();
}

export function flattenQuestionTextForExport(raw: string): string {
  const stripped = stripDustMentionPrefix(raw);
  const flattened = splitGatekeeperAnnotations(stripped)
    .map((segment) => segment.value)
    .join("");
  return capitalizeFirstLetter(flattened.trim());
}

function linkedResources(
  items: Array<{ titre?: string; url?: string }> | undefined
): ResponseExportSource["piecesJointes"] {
  if (!items?.length) return undefined;
  const list = items
    .map((item) => {
      const title = item.titre?.trim();
      const rawUrl = item.url?.trim();
      if (!title || !rawUrl) return null;
      return {
        title,
        url: resolveSidoniDocResourceUrl(rawUrl),
      };
    })
    .filter((item): item is { title: string; url: string } => item !== null);
  return list.length > 0 ? list : undefined;
}

function mapArticleRefs(
  refs: ArticleRef[] | null | undefined
): ResponseExportSource["articleRefs"] {
  if (!refs?.length) return undefined;
  return refs.map((ref) => ({
    nouveau: ref.nouveau,
    ancien: ref.ancien ?? null,
  }));
}

function mapEnrichedOrStructuredSource(
  source: Source | SidoniSource,
  type: SourceType
): ResponseExportSource {
  const sidoni = type === "sidoni" ? (source as SidoniSource) : null;
  const unified = "type" in source ? (source as Source) : null;

  const title =
    (sidoni?.titre?.trim() && sidoni.titre.trim()) ||
    (unified?.nom?.trim() && unified.nom.trim()) ||
    (sidoni?.nom?.trim() && sidoni.nom.trim()) ||
    (typeof sidoni?.resume === "string" && sidoni.resume.trim()) ||
    (unified?.resume?.trim() && unified.resume.trim()) ||
    "Source";

  const sidoniNom = sidoni?.nom?.trim();
  const unifiedNom = unified?.nom?.trim();
  const docNom = sidoniNom || unifiedNom;

  // Prefer client-side reconstruction from `nom` (same as chat fallback),
  // otherwise rewrite any Sidoni doc host URL onto the client doc origin.
  const rawUrl = sidoni?.url ?? unified?.url ?? undefined;
  const url =
    type === "sidoni" && docNom
      ? buildSidoniDocumentUrl(docNom)
      : rawUrl
        ? resolveSidoniDocResourceUrl(rawUrl)
        : undefined;

  const articleRefs =
    mapArticleRefs(sidoni?.refLegifrance) ??
    mapArticleRefs(unified?.refs ?? null);

  return {
    title,
    url,
    links: url ? [{ label: "URL", url }] : [],
    datePublication: sidoni?.datePublication?.trim() || undefined,
    dateMiseAJour: sidoni?.dateMiseAJour?.trim() || undefined,
    date: sidoni?.dateMiseAJour?.trim() || undefined,
    summary: (sidoni?.resume ?? unified?.resume)?.trim() || undefined,
    sourceType: SOURCE_TYPE_LABEL[type] ?? "other",
    documentType: sidoni?.typeDocument?.trim() || undefined,
    classeDocument: sidoni?.classeDocument?.trim() || undefined,
    avertissementDocument: sidoni?.avertissementDocument?.trim() || undefined,
    articleRefs,
    piecesJointes: linkedResources(sidoni?.piecesJointes),
    outilsLies: linkedResources(sidoni?.outilsLies),
  };
}

function dedupeSources(
  sources: ResponseExportSource[]
): ResponseExportSource[] {
  const seen = new Set<string>();
  const result: ResponseExportSource[] = [];
  for (const source of sources) {
    const key = `${source.sourceType ?? ""}|${source.title}|${source.url ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const dedupedLinks = (source.links ?? []).filter(
      (link, index, links) =>
        links.findIndex(
          (candidate) =>
            candidate.url === link.url && candidate.label === link.label
        ) === index
    );
    result.push({ ...source, links: dedupedLinks });
  }
  return result;
}

function groupSourcesIntoCategories(
  sources: ResponseExportSource[]
): ResponseExportSourceCategory[] {
  const byType = new Map<SourceType, ResponseExportSource[]>();
  const other: ResponseExportSource[] = [];

  for (const source of sources) {
    const type = source.sourceType
      ? EXPORT_TYPE_TO_SOURCE_TYPE[source.sourceType]
      : undefined;
    if (!type) {
      other.push(source);
      continue;
    }
    const list = byType.get(type) ?? [];
    list.push(source);
    byType.set(type, list);
  }

  const categories = SOURCE_TYPE_ORDER.filter(
    (type) => (byType.get(type)?.length ?? 0) > 0
  ).map((type) => ({
    type,
    label: SOURCE_CATEGORY_LABELS[type],
    sources: byType.get(type) ?? [],
  }));

  if (other.length > 0) {
    categories.push({
      type: "other",
      label: "Autres sources",
      sources: other,
    });
  }

  return categories;
}

/**
 * Groups enriched/unified sources in chat display order.
 */
export function groupChatSourcesForExport(
  sources: Array<Source | SidoniSource>
): ResponseExportSourceCategory[] {
  const byType = new Map<SourceType, ResponseExportSource[]>();

  for (const source of sources) {
    const type: SourceType =
      "type" in source && source.type ? source.type : "sidoni";
    if (!SOURCE_TYPE_ORDER.includes(type)) continue;
    const mapped = mapEnrichedOrStructuredSource(source, type);
    const list = byType.get(type) ?? [];
    list.push(mapped);
    byType.set(type, list);
  }

  return SOURCE_TYPE_ORDER.filter(
    (type) => (byType.get(type)?.length ?? 0) > 0
  ).map((type) => ({
    type,
    label: SOURCE_CATEGORY_LABELS[type],
    sources: dedupeSources(byType.get(type) ?? []),
  }));
}

function sourcesFromMarkdownSections(markdown: string): ResponseExportSource[] {
  const list: ResponseExportSource[] = [];
  const sourceSectionRegex =
    /###\s+Source\s+([^:]+)\s*:\s*\n([\s\S]*?)(?=\n###\s+Source\s+[^:]+\s*:|$)/g;

  let sectionMatch;
  while ((sectionMatch = sourceSectionRegex.exec(markdown)) !== null) {
    const sourceTypeName = sectionMatch[1]?.trim() ?? "";
    const sourceType: ResponseExportSourceType =
      sourceTypeName === "SIDONI"
        ? "SIDONI"
        : sourceTypeName === "Légifrance"
          ? "Légifrance"
          : "other";
    const sectionContent = sectionMatch[2] ?? "";
    const sourceBlocks = sectionContent.split(/\n---\n/);

    for (const block of sourceBlocks) {
      const titleMatch = block.match(/\*\*Titre\s*:\*\*\s*([^\n]+)/);
      if (!titleMatch?.[1]) continue;
      const urlMatch = block.match(/\*\*URL\s*:\*\*\s*([^\n]+)/);
      const dateMatch = block.match(
        /\*\*Date\s+de\s+mise\s+à\s+jour\s*:\*\*\s*([^\n]+)/
      );
      const summaryMatch = block.match(/\*\*Résumé\s*:\*\*\s*([^\n]+)/);
      list.push({
        title: titleMatch[1].trim(),
        url: urlMatch?.[1]?.trim(),
        links: urlMatch?.[1]?.trim()
          ? [{ label: "URL", url: urlMatch[1].trim() }]
          : [],
        date: dateMatch?.[1]?.trim(),
        dateMiseAJour: dateMatch?.[1]?.trim(),
        summary: summaryMatch?.[1]?.trim(),
        sourceType,
      });
    }
  }

  const fromCitations = sourcesFromCitationMap(markdown);
  return dedupeSources([...list, ...fromCitations]);
}

function sourcesFromCitationMap(markdown: string): ResponseExportSource[] {
  const map = parseCitations(markdown);
  const list: ResponseExportSource[] = [];
  for (const source of map.values()) {
    if (
      !source.title ||
      (source.title === source.id.toUpperCase() &&
        !source.url &&
        !source.summary)
    ) {
      continue;
    }
    list.push({
      title: source.title,
      url: source.url,
      links: source.url ? [{ label: "URL", url: source.url }] : [],
      date: source.date,
      summary: source.summary,
      sourceType: source.sourceType ?? "other",
    });
  }
  return list;
}

function buildFromStructured(
  structured: ApiResponse,
  questionText: string,
  exportedAt: string,
  enrichedSources?: Array<Source | SidoniSource>
): ResponseExportDocument {
  const sourceCategories =
    enrichedSources && enrichedSources.length > 0
      ? groupChatSourcesForExport(enrichedSources)
      : groupChatSourcesForExport(structured.sources);

  return {
    title: RESPONSE_EXPORT_TITLE,
    exportedAt,
    question: { text: questionText },
    answerMarkdown: stripCitationMarkers(structured.reponse),
    sourceCategories,
    disclaimer: RESPONSE_EXPORT_DISCLAIMER,
  };
}

function buildFromMarkdown(
  content: string,
  questionText: string,
  exportedAt: string
): ResponseExportDocument {
  const flat = sourcesFromMarkdownSections(content);
  const answerMarkdown = stripCitationMarkers(
    stripMarkdownSourceSections(content)
  );
  return {
    title: RESPONSE_EXPORT_TITLE,
    exportedAt,
    question: { text: questionText },
    answerMarkdown,
    sourceCategories: groupSourcesIntoCategories(flat),
    disclaimer: RESPONSE_EXPORT_DISCLAIMER,
  };
}

export interface BuildResponseExportInput {
  assistantMessage: Message;
  questionText: string | null | undefined;
  exportedAt?: string;
  /**
   * Sources as already presented in the chat (enriched Sidoni when available).
   * When omitted, falls back to structured JSON / markdown parsing only.
   */
  enrichedSources?: Array<Source | SidoniSource>;
}

/**
 * Builds a durable export document from an assistant SidoniSearch message.
 * Prefers structured JSON (`reponse` + `sources`) when present.
 */
export function buildResponseExportDocument({
  assistantMessage,
  questionText,
  exportedAt = new Date().toISOString(),
  enrichedSources,
}: BuildResponseExportInput): ResponseExportDocument {
  const question = flattenQuestionTextForExport(questionText?.trim() || "");
  const structured = parseStructuredResponse(assistantMessage.content);

  if (structured?.reponse) {
    return buildFromStructured(
      structured,
      question,
      exportedAt,
      enrichedSources
    );
  }

  return buildFromMarkdown(assistantMessage.content, question, exportedAt);
}
